import TagWithOverflow from "@/components/tagWithOverflow/tagWithOverflow";
import PatientStatusHeaderDropdown from "./PatientStatusHeaderDropdown";
import AddWithCircle from "@/svg/AddWithCircle";
import OpenInNewWindow from "@/svg/OpenInNewWindow";
import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import { getEligibilityStatusBadgeColors } from "../../../utils/eligibilityStatusBadges";
import "./HeaderDetails.scss";

const ONCO_EMR_BASE = "https://secure2.oncoemr.com";
const ONCO_EMR_LOCATION = "LH_06A43HYPY47HHDGK2M6N";

/** Props for the collapsed patient identity strip. */
interface HeaderDetailsProps {
  name: string;
  mrn: string;
  dobWithAge: string;
  sex: string;
  eligibilityStatus: string;
  eligibilityTier: string;
  screeningSummary: string;
  additionalStudies: string[];
  fhirIdentifier: string | null;
}

/**
 * Thin vertical gray divider between header fields.
 *
 * @returns Vertical divider line
 */
const HeaderDivider = () => {
  return (
    <div className="divider shrink-0 self-center" aria-hidden>
      <svg
        width="1"
        height="12"
        viewBox="0 0 1 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <line x1="0.5" x2="0.5" y2="12" stroke="#C7C7C7" />
      </svg>
    </div>
  );
};

/**
 * Open the patient's OncoEMR summary in a new tab when a FHIR id is present.
 *
 * @param fhirIdentifier - Flatiron FHIR Patient.id
 * @param mrn - Patient MRN used in the missing-id warning
 * @returns Nothing
 */
function openOncoEmrSummary(fhirIdentifier: string | null, mrn: string): void {
  if (!fhirIdentifier) {
    window.alert(`No FHIR identifier available for patient ${mrn}`);
    return;
  }
  const patientId = fhirIdentifier.split(".")[0].replaceAll("--", "_");
  window.open(
    `${ONCO_EMR_BASE}/nav/summary?PID=${patientId}&locationId=${ONCO_EMR_LOCATION}`,
    "_blank",
  );
}

/**
 * Identity strip for the single-patient eligibility details header.
 *
 * @param props - {@link HeaderDetailsProps}
 * @returns Header identity fields
 */
const HeaderDetails = ({
  name,
  mrn,
  dobWithAge,
  sex,
  eligibilityStatus,
  eligibilityTier,
  screeningSummary,
  additionalStudies,
  fhirIdentifier,
}: HeaderDetailsProps) => {
  const eligibilityStatusColors = getEligibilityStatusBadgeColors(
    eligibilityStatus,
  );

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
      <div className="patient-name shrink-0 font-bold text-base leading-5">
        {name}
      </div>
      <HeaderDivider />
      <div className="patient-mrn shrink-0 text-x-tiny font-normal leading-5 text-primaryGray-2">
        {mrn}
      </div>
      <HeaderDivider />
      <div className="patient-dob-with-age shrink-0 text-x-tiny font-normal leading-5 text-primaryGray-2">
        {dobWithAge}
      </div>
      <HeaderDivider />
      <div className="patient-sex shrink-0 text-x-tiny font-normal leading-5 text-primaryGray-2">
        {sex}
      </div>
      <HeaderDivider />
      <div className="patient-eligibility-tag flex shrink-0 items-center">
        <Tags label={eligibilityStatus} {...eligibilityStatusColors} />
      </div>
      <HeaderDivider />
      <div className="patient-tier-tag flex shrink-0 items-center">
        <Tags
          label={eligibilityTier}
          backgroundColor="#E8F1FF"
          textColor="#0056D6"
          shouldTruncate
        />
      </div>
      {/* <HeaderDivider /> */}
      {/* <div className="patient-screening-tag flex shrink-0 items-center">
        <Tags label={screeningSummary} />
      </div> */}
      <HeaderDivider />
      <div className="evaluation-tag flex shrink-0 items-center">
        <PatientStatusHeaderDropdown />
      </div>
      <HeaderDivider />
      <div className="clinical-trials-details flex shrink-0 items-center gap-2">
        <TagWithOverflow
          tags={additionalStudies}
          maxVisibleTags={2}
          tagClassName="clinical-trial-tag"
          placement="bottom"
        />
        <button
          type="button"
          className="flex items-center justify-center hover:scale-105 hover:cursor-pointer transition-all"
          onClick={() => {}}
          aria-label="Add clinical trial"
        >
          <AddWithCircle />
        </button>
      </div>
      <HeaderDivider />
      <div className="open-in-onco-emr flex shrink-0 items-center gap-1">
        <span className="text-x-tiny font-semibold text-tertiaryBlue-4">
          OncoEMR
        </span>
        <button
          type="button"
          className="flex items-center justify-center hover:scale-105 hover:cursor-pointer transition-all"
          onClick={() => openOncoEmrSummary(fhirIdentifier, mrn)}
          aria-label="Open patient in OncoEMR"
        >
          <OpenInNewWindow />
        </button>
      </div>
    </div>
  );
};

export default HeaderDetails;
